import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Login from '@/pages/Login';
import AdminDashboard from '@/pages/AdminDashboard';
import TrainingsPage from '@/pages/TrainingsPage';
import LearnersPage from '@/pages/LearnersPage';
import TrainersPage from '@/pages/TrainersPage';
import PartnershipsPage from '@/pages/PartnershipsPage';
import ResourcesPage from '@/pages/ResourcesPage';
import RegistrationsPage from '@/pages/RegistrationsPage';
import PublicCatalog from '@/pages/PublicCatalog';
import PaymentSuccess from '@/pages/PaymentSuccess';
import StatisticsPage from '@/pages/StatisticsPage';
import StudentPortal from '@/pages/StudentPortal';
import PartnerPortal from '@/pages/PartnerPortal';
import PartnerProposalsPage from '@/pages/PartnerProposalsPage';
import HomePage from '@/pages/HomePage';
import PublicResources from '@/pages/PublicResources';
import ResourceOrdersPage from '@/pages/ResourceOrdersPage';
import MentionsLegales from '@/pages/MentionsLegales';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('insitumed_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    verifyAuth();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('insitumed_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/admin/verify`);
      setAdmin(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth verification failed:', error);
      localStorage.removeItem('insitumed_token');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, adminData) => {
    localStorage.setItem('insitumed_token', token);
    setAdmin(adminData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('insitumed_token');
    setAdmin(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center">
        <div className="text-violet-600 text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/catalogue" element={<PublicCatalog />} />
        <Route path="/ressources" element={<PublicResources />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/mon-espace" element={<StudentPortal />} />
        <Route path="/partenaire" element={<PartnerPortal />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        
        {/* Admin Routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/admin" replace />
            ) : (
              <Login onLogin={handleLogin} />
            )
          }
        />
        
        <Route
          path="/admin"
          element={
            isAuthenticated ? (
              <AdminDashboard admin={admin} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route
          path="/admin/formations"
          element={
            isAuthenticated ? (
              <TrainingsPage admin={admin} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route
          path="/admin/apprenants"
          element={
            isAuthenticated ? (
              <LearnersPage admin={admin} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route
          path="/admin/formateurs"
          element={
            isAuthenticated ? (
              <TrainersPage admin={admin} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route
          path="/admin/partenariats"
          element={
            isAuthenticated ? (
              <PartnershipsPage admin={admin} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route
          path="/admin/ressources"
          element={
            isAuthenticated ? (
              <ResourcesPage admin={admin} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route
          path="/admin/inscriptions"
          element={
            isAuthenticated ? (
              <RegistrationsPage admin={admin} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route
          path="/admin/statistiques"
          element={
            isAuthenticated ? (
              <StatisticsPage admin={admin} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route
          path="/admin/propositions"
          element={
            isAuthenticated ? (
              <PartnerProposalsPage admin={admin} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route
          path="/admin/commandes"
          element={
            isAuthenticated ? (
              <ResourceOrdersPage admin={admin} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        
        <Route path="/" element={<HomePage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;